import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { categoricalBooleanDataSocket, numericDataSocket } from "../socket_types"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../tile_grid"
import { BaseComponent } from "./base_component"
import { ExposeToggleControl } from "../controls/expose_toggle_control"
import { PayoffMatrixControl } from "../controls/payoff_matrix"
import { ProjectProperties } from "."
import { maskFromExtentAndShape } from "../bounding_box"
import {
  applyPayoffAxesToNodeData,
  axesFromInputGrid,
  inferPayoffAxesFromConnections,
  lookupPayoffForCells,
  readCellValue,
  type PayoffAxesConfig,
} from "../payoff_matrix_logic"

type PayoffInputGrid = BooleanTileGrid | CategoricalTileGrid

function isPayoffInputGrid(value: unknown): value is PayoffInputGrid {
  return value instanceof BooleanTileGrid || value instanceof CategoricalTileGrid
}

export const PAYOFF_MATRIX_COMPONENT_NAME = "Payoff matrix"

export class BooleanPayoffMatrixComponent extends BaseComponent {
  projectProperties: ProjectProperties
  private static connectionHooksByNodeId = new Set<number>()

  constructor(projectProperties: ProjectProperties) {
    super(PAYOFF_MATRIX_COMPONENT_NAME)
    this.category = "Set operations"
    this.contextMenuName = "Payoff matrix (boolean/categorical → numeric)"
    this.projectProperties = projectProperties
  }

  async builder(node: Node) {
    node.addInput(new Input("a", "A", categoricalBooleanDataSocket))
    node.addInput(new Input("b", "B", categoricalBooleanDataSocket))
    node.addOutput(new Output("out", "Numeric payoff", numericDataSocket))

    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }
    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }

    if (!node.controls.has("payoffMatrix")) {
      node.addControl(new PayoffMatrixControl(this.editor))
    }

    this.seedPayoffAxesFromConnections(node)

    if (this.editor && !BooleanPayoffMatrixComponent.connectionHooksByNodeId.has(node.id)) {
      BooleanPayoffMatrixComponent.connectionHooksByNodeId.add(node.id)
      const refreshOnConnectionChange = (connection: any) => {
        if (connection != null && !this.connectionTouchesPayoffInput(node, connection)) {
          return
        }
        // Defer until Rete has finished updating input.connections (cat → bool rewires).
        queueMicrotask(() => this.seedPayoffAxesFromConnections(node))
      }
      this.editor.on("connectioncreated", refreshOnConnectionChange)
      this.editor.on("connectionremoved", refreshOnConnectionChange)
      this.editor.on("noderemoved", (removedNode: any) => {
        if (removedNode != null && removedNode.id === node.id) {
          BooleanPayoffMatrixComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  private connectionTouchesPayoffInput(node: Node, connection: any): boolean {
    if (connection == null || connection.input == null) {
      return false
    }
    const inputNode = connection.input.node
    const inputNodeId = typeof inputNode === "object" && inputNode != null ? inputNode.id : inputNode
    if (inputNodeId !== node.id) {
      return false
    }
    const key = String(connection.input.key != null ? connection.input.key : "")
    return key === "a" || key === "b"
  }

  /** Update matrix rows/columns from upstream wiring (same pattern as categorical → numeric). */
  private seedPayoffAxesFromConnections(node: Node) {
    if (!this.editor) return
    const data = node.data as Record<string, unknown>
    const axes = inferPayoffAxesFromConnections(this.editor, node)
    applyPayoffAxesToNodeData(data, axes)
    this.refreshPayoffControl(node)
    node.update()
  }

  private syncPayoffMatrixAxes(editorNode: Node, aIn: PayoffInputGrid, bIn: PayoffInputGrid): PayoffAxesConfig {
    const axes: PayoffAxesConfig = {
      a: axesFromInputGrid(aIn),
      b: axesFromInputGrid(bIn),
    }
    applyPayoffAxesToNodeData(editorNode.data as Record<string, unknown>, axes)
    return axes
  }

  private refreshPayoffControl(editorNode: Node) {
    const control = editorNode.controls.get("payoffMatrix") as { update?: () => Promise<void> } | undefined
    if (control && typeof control.update === "function") {
      void control.update()
    }
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) return

    const aIn = inputs["a"]?.[0]
    const bIn = inputs["b"]?.[0]

    if (!isPayoffInputGrid(aIn) || !isPayoffInputGrid(bIn)) {
      editorNode.meta.errorMessage = "Requires two boolean or categorical datasets (A and B)"
      editorNode.update()
      return
    }

    if (aIn instanceof CategoricalTileGrid && axesFromInputGrid(aIn).length === 0) {
      editorNode.meta.errorMessage = "Categorical input A has no category labels"
      editorNode.update()
      return
    }
    if (bIn instanceof CategoricalTileGrid && axesFromInputGrid(bIn).length === 0) {
      editorNode.meta.errorMessage = "Categorical input B has no category labels"
      editorNode.update()
      return
    }

    delete editorNode.meta.errorMessage

    const data = editorNode.data as Record<string, unknown>
    const axes = this.syncPayoffMatrixAxes(editorNode, aIn, bIn)
    this.refreshPayoffControl(editorNode)

    const { zoom, x, y, width, height } = aIn
    const out = new NumericTileGrid(zoom, x, y, width, height, NaN)

    for (let i = x; i < x + width; i++) {
      for (let j = y; j < y + height; j++) {
        const aVal = readCellValue(aIn, i, j, zoom)
        const bVal = readCellValue(bIn, i, j, zoom)
        out.set(i, j, lookupPayoffForCells(aVal, bVal, data, axes))
      }
    }

    const mask = await maskFromExtentAndShape(this.projectProperties)
    if (mask) {
      out.iterate((x, y) => {
        if (!mask.get(x, y, out.zoom)) out.set(x, y, NaN)
      })
    }

    out.name = (editorNode.data.name as string)?.trim() || "Payoff matrix"

    outputs["out"] = out
    editorNode.update()
  }
}
